/**
 * Napi nézet — handover rev7 §11.7: "Orders, guests and portions per date
 * against their limits; set the three limits per date, closures."
 *
 * The three limits are §8.1's, and each one on a date is either the date's own
 * or the register's default. The column says which, because a kitchen manager
 * raising a cap needs to know whether they are changing one day or looking at
 * a number that moves when somebody changes `portion_cap_default`.
 *
 * The figures come from `public.capacity_for_date` — the same function the
 * blocking rules of §11.3 use — so the console cannot show a limit submission
 * does not enforce.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asDate, fmt, Loading, useConsoleAction, useConsoleData } from '../common.tsx'

interface Day {
  date: string
  is_business_day: boolean | null
  holiday_name: string | null
  kitchen_closed: boolean
  is_clash_date: boolean
  in_calendar: boolean
  note: string | null
  set_by: string | null
  limit_source: Record<'portion_cap' | 'max_orders_per_day' | 'max_covers_per_day', string>
  portion_cap: number | null
  max_orders_per_day: number | null
  max_covers_per_day: number | null
  soft_threshold: number | null
  committed_portions: number
  committed_orders: number
  committed_covers: number
  orders: { id: string; ref: string; guests: number; event_name: string | null }[]
}

const todayIso = (): string => new Date().toISOString().slice(0, 10)

export function Against(
  { used, limit, soft }: { used: number; limit: number | null; soft?: number | null },
): JSX.Element {
  const over = limit !== null && used >= limit
  const warm = !over && soft != null && used >= soft
  return (
    <span className={over ? 'tag stop' : warm ? 'tag warn' : 'tag'}>
      {fmt.n(used)} / {limit === null ? '—' : fmt.n(limit)}
    </span>
  )
}

export function Day(): JSX.Element {
  const [from, setFrom] = useState(todayIso())
  const [to, setTo] = useState('')
  const [applied, setApplied] = useState<{ from: string; to: string }>({ from: todayIso(), to: '' })
  const { data, error, loading, reload } = useConsoleData<{ days: Day[] }>('day_view', {
    from: applied.from,
    ...(applied.to === '' ? {} : { to: applied.to }),
  })
  const { run, busy, error: actionError } = useConsoleAction()
  const [editing, setEditing] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})

  async function save(day: Day): Promise<void> {
    const clear: string[] = []
    const args: Record<string, unknown> = { date: day.date }
    for (
      const [field, name] of [
        ['portion_cap', 'portion_cap'],
        ['max_orders', 'max_orders_per_day'],
        ['max_covers', 'max_covers_per_day'],
      ] as const
    ) {
      const value = (form[`${day.date}.${field}`] ?? '').trim()
      if (value === '') clear.push(name)
      else args[field] = Number(value)
    }
    args.kitchen_closed = (form[`${day.date}.closed`] ?? String(day.kitchen_closed)) === 'true'
    args.note = form[`${day.date}.note`] ?? ''
    if (clear.length > 0) args.clear = clear
    if (await run('day_limits_set', args)) {
      setEditing(null)
      reload()
    }
  }

  function startEditing(day: Day): void {
    setEditing(day.date)
    setForm({
      [`${day.date}.portion_cap`]: day.limit_source.portion_cap === 'date'
        ? String(day.portion_cap ?? '')
        : '',
      [`${day.date}.max_orders`]: day.limit_source.max_orders_per_day === 'date'
        ? String(day.max_orders_per_day ?? '')
        : '',
      [`${day.date}.max_covers`]: day.limit_source.max_covers_per_day === 'date'
        ? String(day.max_covers_per_day ?? '')
        : '',
      [`${day.date}.closed`]: String(day.kitchen_closed),
      [`${day.date}.note`]: day.note ?? '',
    })
  }

  return (
    <section data-testid="view-day">
      <h2>Napi nézet</h2>
      <p className="lead">
        §8.1: a három napi korlát dátumonként állítható. Üresen hagyva a regiszter alapértéke
        érvényes, és az változik együtt minden ilyen nappal.
      </p>

      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          setApplied({ from, to })
        }}
      >
        <label htmlFor="day-from">Ettől</label>
        <input
          id="day-from"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          data-testid="day-from"
        />
        <label htmlFor="day-to">Eddig (üresen 20 nap)</label>
        <input
          id="day-to"
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          data-testid="day-to"
        />
        <div className="actions">
          <button type="submit" className="primary" data-testid="day-apply">Mutasd</button>
        </div>
      </form>

      {/* §11.6: the two kitchen documents that are about a day rather than an
          order. K5 is the production day; K7 starts on the day shown and runs
          `forecast_bd` business days from it. */}
      <article className="card">
        <h3>Konyhai nyomtatási nézetek (§11.6)</h3>
        <div className="actions">
          <Link
            to={`/staff/nyomtatas/K5?nap=${encodeURIComponent(applied.from)}`}
            target="_blank"
            data-testid="print-k5"
          >
            <button type="button">K5 · gyártási emlékeztető</button>
          </Link>
          <Link
            to={`/staff/nyomtatas/K7?nap=${encodeURIComponent(applied.from)}`}
            target="_blank"
            data-testid="print-k7"
          >
            <button type="button">K7 · előrejelzés és konyhai naptár</button>
          </Link>
        </div>
        <p className="note">
          A K5 a gyártási napra készül (§8.2), nem a rendezvény napjára: a fenti <em>Ettől</em>
          {' '}dátumot használja.
        </p>
      </article>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && (
        <table>
          <thead>
            <tr>
              <th>Nap</th>
              <th className="num">Rendelés</th>
              <th className="num">Vendég</th>
              <th className="num">Adag</th>
              <th>Állapot</th>
              <th>Rendelések</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.days.map((day) => (
              <tr key={day.date} data-testid={`day-${day.date}`}>
                <td>
                  {asDate(day.date, true)}
                  {day.holiday_name !== null && <div className="tag">{day.holiday_name}</div>}
                  {!day.in_calendar && (
                    <div className="tag warn">nincs a naptárban (§8.1)</div>
                  )}
                </td>
                <td className="num">
                  <Against used={day.committed_orders} limit={day.max_orders_per_day} />
                  <div className="tag">{day.limit_source.max_orders_per_day === 'date' ? 'napi' : 'alap'}</div>
                </td>
                <td className="num">
                  <Against used={day.committed_covers} limit={day.max_covers_per_day} />
                  <div className="tag">{day.limit_source.max_covers_per_day === 'date' ? 'napi' : 'alap'}</div>
                </td>
                <td className="num">
                  <Against
                    used={day.committed_portions}
                    limit={day.portion_cap}
                    soft={day.soft_threshold}
                  />
                  <div className="tag">{day.limit_source.portion_cap === 'date' ? 'napi' : 'alap'}</div>
                </td>
                <td>
                  {day.is_business_day === false && <span className="tag">nem munkanap</span>}
                  {day.kitchen_closed && <span className="tag stop">konyha zárva</span>}
                  {day.is_clash_date && <span className="tag warn">ütközéses nap</span>}
                  {day.note !== null && <div className="tag">{day.note}</div>}
                  {day.set_by !== null && <div className="tag">{day.set_by}</div>}
                </td>
                <td>
                  {day.orders.map((order) => (
                    <div key={order.id}>
                      <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
                      {' · '}
                      {fmt.n(order.guests)} fő
                    </div>
                  ))}
                </td>
                <td>
                  {editing === day.date
                    ? (
                      <div className="card" data-testid={`day-form-${day.date}`}>
                        <label htmlFor={`cap-${day.date}`}>Adagkeret (üres = alapérték)</label>
                        <input
                          id={`cap-${day.date}`}
                          type="number"
                          value={form[`${day.date}.portion_cap`] ?? ''}
                          onChange={(event) =>
                            setForm((p) => ({ ...p, [`${day.date}.portion_cap`]: event.target.value }))}
                          data-testid={`day-cap-${day.date}`}
                        />
                        <label htmlFor={`ord-${day.date}`}>Rendelés / nap</label>
                        <input
                          id={`ord-${day.date}`}
                          type="number"
                          value={form[`${day.date}.max_orders`] ?? ''}
                          onChange={(event) =>
                            setForm((p) => ({ ...p, [`${day.date}.max_orders`]: event.target.value }))}
                        />
                        <label htmlFor={`cov-${day.date}`}>Vendég / nap</label>
                        <input
                          id={`cov-${day.date}`}
                          type="number"
                          value={form[`${day.date}.max_covers`] ?? ''}
                          onChange={(event) =>
                            setForm((p) => ({ ...p, [`${day.date}.max_covers`]: event.target.value }))}
                        />
                        <label htmlFor={`closed-${day.date}`}>Konyha</label>
                        <select
                          id={`closed-${day.date}`}
                          value={form[`${day.date}.closed`] ?? 'false'}
                          onChange={(event) =>
                            setForm((p) => ({ ...p, [`${day.date}.closed`]: event.target.value }))}
                          data-testid={`day-closed-${day.date}`}
                        >
                          <option value="false">nyitva</option>
                          <option value="true">zárva</option>
                        </select>
                        <label htmlFor={`note-${day.date}`}>Megjegyzés</label>
                        <input
                          id={`note-${day.date}`}
                          type="text"
                          value={form[`${day.date}.note`] ?? ''}
                          onChange={(event) =>
                            setForm((p) => ({ ...p, [`${day.date}.note`]: event.target.value }))}
                        />
                        <div className="actions">
                          <button
                            type="button"
                            className="primary"
                            disabled={busy}
                            onClick={() => void save(day)}
                            data-testid={`day-save-${day.date}`}
                          >
                            Mentés
                          </button>
                          <button type="button" onClick={() => setEditing(null)}>Mégse</button>
                        </div>
                      </div>
                    )
                    : (
                      <button
                        type="button"
                        onClick={() => startEditing(day)}
                        data-testid={`day-edit-${day.date}`}
                      >
                        Korlátok
                      </button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
